import { createHash, getHashes } from 'crypto';

import { defang as defangString, refang as refangString } from 'fanger';
import levenshtein from 'js-levenshtein';
import jsonata from 'jsonata';
import _ from 'lodash';
import { v4 } from 'uuid';

import {
  CaseSensitivityBasic,
  caseSensitivityToRegexFlag,
  CharacterEncoding,
  CharacterEncodingBasic,
  escapeSpecialRegexCharacters,
  HashAlgorithm,
  HashAlgorithmBasic,
  normalizeCharacterEncoding,
  parseNumberOrThrow
} from '../common';
import { InvalidHashAlgorithmError, requireInput } from '../errors';

/**
 * Convert a string to base 64 representation.
 * @param text The text to encode to base64 representation.
 * @param textEncoding The character encoding of the input text (default = UTF-8).
 *
 * @returns The text string encoded to base64 representation.
 *
 * @throws `InputRequiredError` "<input>" is a required input but was not provided
 */
export function base64Encode<E extends `${CharacterEncoding}`, EB extends `${CharacterEncodingBasic}`>(
  text: string,
  textEncoding?: CharacterEncoding | CharacterEncodingBasic | E | EB
): string {
  requireInput(text, 'text');
  return Buffer.from(text, normalizeCharacterEncoding(textEncoding)).toString('base64');
}

/**
 * Convert a string from base 64 representation.
 * @param text The text to decode from base64 representation.
 * @param outputEncoding The character encoding of the output text (default = UTF-8).
 *
 * @returns The text string decoded from base64 representation.
 *
 * @throws `InputRequiredError` "<input>" is a required input but was not provided
 */
export function base64Decode<E extends `${CharacterEncoding}`, EB extends `${CharacterEncodingBasic}`>(
  text: string,
  outputEncoding?: CharacterEncoding | CharacterEncodingBasic | E | EB
): string {
  requireInput(text, 'text');

  return Buffer.from(text, 'base64').toString(normalizeCharacterEncoding(outputEncoding));
}

/**
 * Determine if a string contains at least one occurrence of a search term.
 * @param text The string to search.
 * @param search The search term.
 * @param caseSensitivity Search term case sensitivity.
 *
 * @returns True if the search term is found at least once. False otherwise.
 *
 * @throws `InputRequiredError` "<input>" is a required input but was not provided
 */
export function containsBasic<C extends `${CaseSensitivityBasic}`>(
  text: string,
  search: string,
  caseSensitivity?: CaseSensitivityBasic | C
): boolean {
  requireInput(text, 'text');
  requireInput(search, 'search');

  if (text === '' || search === '') {
    return false;
  }

  return (
    text.search(
      new RegExp(escapeSpecialRegexCharacters(search ? search : ''), caseSensitivityToRegexFlag(caseSensitivity))
    ) >= 0
  );
}

/**
 * Find occurrences of a search term in a string and returns an array of all indices where the search term is located.
 * @param text The string to search.
 * @param search The search term.
 * @param caseSensitivity Search term case sensitivity.
 *
 * @returns An array of all indices where the search term is located.
 *
 * @throws `InputRequiredError` "<input>" is a required input but was not provided
 */
export function matchBasic<C extends `${CaseSensitivityBasic}`>(
  text: string,
  search: string,
  caseSensitivity?: CaseSensitivityBasic | C
): number[] {
  requireInput(text, 'text');
  requireInput(search, 'search');

  if (text === '' || search === '') {
    return [];
  }

  const indices: number[] = [];
  for (const match of text.matchAll(
    new RegExp(escapeSpecialRegexCharacters(search ? search : ''), caseSensitivityToRegexFlag(caseSensitivity) + 'g')
  )) {
    if (match.index !== undefined) {
      indices.push(match.index);
    }
  }
  return indices;
}

/**
 * Find occurrences of a search term in a string and replaces each occurrence with another string.
 * @param text The string to search.
 * @param search The search term.
 * @param caseSensitivity Search term case sensitivity.
 * @param replacement The replacement value.
 *
 * @returns The input string with each occurrence of the search term replaced with the replacement string up to the replacement limit, if specified.
 *
 * @throws `InputRequiredError` "<input>" is a required input but was not provided
 */
export function replaceBasic<C extends `${CaseSensitivityBasic}`>(
  text: string,
  search: string,
  caseSensitivity?: CaseSensitivityBasic | C,
  replacement: string = ''
): string {
  requireInput(text, 'text');
  requireInput(search, 'search');

  if (text === '' || search === '') {
    return text;
  }

  return text.replace(
    new RegExp(escapeSpecialRegexCharacters(search ? search : ''), caseSensitivityToRegexFlag(caseSensitivity) + 'g'),
    replacement
  );
}

/**
 * Find occurrences a delimiter in a string and splits the string into an array of substrings for each occurrence.
 * @param text The string to search.
 * @param delimiter The delimiter with which to split the string.
 *
 * @returns An array of strings containing the segments of the input string that were split on each matching occurrence of the search term up to the split limit, if specified
 *
 * @throws `InputRequiredError` "<input>" is a required input but was not provided
 */
export function split(text: string, delimiter: string): string[] {
  requireInput(text, 'text');
  requireInput(delimiter, 'delimiter');

  return text.split(delimiter);
}

/**
 * Replaces and/or surrounds certain characters in a string to render it unactionable (e.g. non-clickable).
 * @param text The string to defang.
 *
 * @returns The defanged input string.
 *
 * @throws `InputRequiredError` "<input>" is a required input but was not provided
 */
export function defang(text: string): string {
  requireInput(text, 'text');

  return defangString(text);
}

/**
 * Restore a defanged string to its original value to re-render it actionable (e.g. clickable).
 * @param text The string to refang.
 *
 * @returns The refanged input string.
 *
 * @throws `InputRequiredError` "<input>" is a required input but was not provided
 */
export function refang(text: string): string {
  requireInput(text, 'text');

  return refangString(text);
}

/**
 * Convert stringified JSON text to a JSON value.
 * @param text The stringified text to convert to a JSON value.
 *
 * @returns A JSON value obtained by parsing the stringified input text.
 *
 * @throws `InputRequiredError` "<input>" is a required input but was not provided
 */
export function jsonParse(text: string): object {
  requireInput(text, 'text');

  return JSON.parse(text);
}

/**
 * Convert URL-unsafe characters in a given string to URL-safe characters.
 * @param text The string to URL encode.
 *
 * @returns An output string that is URL encoded.
 *
 * @throws `InputRequiredError` "<input>" is a required input but was not provided
 */
export function urlEncode(text: string): string {
  requireInput(text, 'text');

  return encodeURIComponent(text).replace(/%20/g, '+');
}

/**
 * Convert URL-safe characters in a given string to URL-unsafe characters.
 * @param text The string to URL decode.
 *
 * @returns An output string that is URL decoded.
 *
 * @throws `InputRequiredError` "<input>" is a required input but was not provided
 */
export function urlDecode(text: string): string {
  requireInput(text, 'text');

  return decodeURIComponent(text.replace(/\+/g, '%20'));
}

/**
 * Find the edit/Levenshtein distance between two strings.
 * @param source The source string.
 * @param target The target string.
 *
 * @returns A Value indicating edit/Levenshtein distance between two strings.
 *
 * @throws `InputRequiredError` "<input>" is a required input but was not provided
 */
export function levenshteinDistance(source: string, target: string): number {
  requireInput(source, 'source');
  requireInput(target, 'target');

  return levenshtein(source, target);
}

/**
 * Remove all whitespace (tabs, spaces, and newlines) from both the left and right side of text. Spaces in between
 * words will not be removed.
 * @param source The string to trim.
 *
 * @returns The input string stripped of whitespace from both its beginning and end.
 *
 */
export function strip(source: string): string {
  return source.trim();
}

/**
 * Generate a v4 universally unique identifier (UUID).
 *
 * @returns The unique identifier.
 */
export function uuidv4(): string {
  return v4();
}

/**
 * Compute that hash of a value.
 * @param source The value to hash.
 * @param algorithm The hash algorithm.
 *
 * @returns The hashed value in hex.
 *
 * @throws `InvalidHashType` "<type>" is not a valid hash type.
 */
export function hash<H extends `${HashAlgorithm}`, HB extends `${HashAlgorithmBasic}`>(
  source: string,
  algorithm: HashAlgorithm | HashAlgorithmBasic | H | HB | string
): string {
  let normalizedAlgorithm: string | undefined;

  switch (algorithm) {
    case HashAlgorithm.md5:
    case HashAlgorithmBasic.md5:
      normalizedAlgorithm = 'md5';
      break;
    case HashAlgorithm.sha1:
    case HashAlgorithmBasic.sha1:
      normalizedAlgorithm = 'sha1';
      break;
    case HashAlgorithm.sha2224:
    case HashAlgorithmBasic.sha2224:
      normalizedAlgorithm = 'sha224';
      break;
    case HashAlgorithm.sha2256:
    case HashAlgorithmBasic.sha2256:
      normalizedAlgorithm = 'sha256';
      break;
    case HashAlgorithm.sha2384:
    case HashAlgorithmBasic.sha2384:
      normalizedAlgorithm = 'sha384';
      break;
    case HashAlgorithm.sha2512:
    case HashAlgorithmBasic.sha2512:
      normalizedAlgorithm = 'sha512';
      break;
    case HashAlgorithm.sha3224:
    case HashAlgorithmBasic.sha3224:
      normalizedAlgorithm = 'sha3-224';
      break;
    case HashAlgorithm.sha3256:
    case HashAlgorithmBasic.sha3256:
      normalizedAlgorithm = 'sha3-256';
      break;
    case HashAlgorithm.sha3384:
    case HashAlgorithmBasic.sha3384:
      normalizedAlgorithm = 'sha3-384';
      break;
    case HashAlgorithm.sha3512:
    case HashAlgorithmBasic.sha3512:
      normalizedAlgorithm = 'sha3-512';
      break;
  }

  if (!normalizedAlgorithm) {
    if (!getHashes().includes(algorithm)) {
      throw new InvalidHashAlgorithmError();
    }

    normalizedAlgorithm = algorithm;
  }

  return createHash(normalizedAlgorithm).update(source).digest('hex');
}

/**
 * Pad a string on the left and right sides if it's shorter than length. Padding characters are truncated if they can't be evenly divided by length.
 * @param source The string to pad.
 * @param length The padding length.
 * @param chars The string used as padding.
 *
 * @returns The padded string.
 *
 * @throws `InvalidNumberError` "<length>" is not a valid number.
 */
export function pad(source: string, length: number | string, chars?: string): string {
  return _.pad(source, parseNumberOrThrow(length, 'length'), !chars ? ' ' : chars);
}

/**
 * Pad a string on the left side if it's shorter than length. Padding characters are truncated if they can't be evenly divided by length.
 * @param source The string to pad.
 * @param length The padding length.
 * @param chars The string used as padding.
 *
 * @returns The padded string.
 *
 * @throws `InvalidNumberError` "<length>" is not a valid number.
 */
export function padStart(source: string, length: number | string, chars?: string): string {
  return _.padStart(source, parseNumberOrThrow(length, 'length'), !chars ? ' ' : chars);
}

/**
 * Pad a string on the right side if it's shorter than length. Padding characters are truncated if they can't be evenly divided by length.
 * @param source The input string to pad.
 * @param length The padding length.
 * @param chars The string used as padding.
 *
 * @returns The padded string.
 *
 * @throws `InvalidNumberError` "<length>" is not a valid number.
 */
export function padEnd(source: string, length: number | string, chars?: string): string {
  return _.padEnd(source, parseNumberOrThrow(length, 'length'), !chars ? ' ' : chars);
}

/**
 * Repeat a string N times.
 * @param source The string to repeat.
 * @param repeatNumber The number of times to repeat the string.
 *
 * @returns The repeated string.
 *
 * @throws `InvalidNumberError` "<length>" is not a valid number.
 */
export function repeat(source: string, repeatNumber: number | string): string {
  return _.repeat(source, parseNumberOrThrow(repeatNumber, 'repeatNumber'));
}

/**
 * Convert a string to camel case.
 * @param source The string to convert.
 *
 * @returns The camel-cased input string.
 *
 */
export function camelCase(source: string): string {
  return _.camelCase(source);
}

/**
 * Convert the first character of a string to upper case and the remaining to lower case.
 * @param source The string to convert.
 *
 * @returns The capitalized string.
 *
 */
export function capitalize(source: string): string {
  return _.capitalize(source);
}

/**
 * Check if a string starts with another.
 * @param source The string to search.
 * @param target The search value.
 *
 * @returns True if the input string starts with the search string. False otherwise.
 */
export function startsWith(source: string, target: string): boolean {
  return _.startsWith(source, target);
}

/**
 * Check if string ends with another.
 * @param source The string to search.
 * @param target The search value.
 *
 * @returns Returns true if string ends with target, else false.
 */
export function endsWith(source: string, target: string): boolean {
  return _.endsWith(source, target);
}

/**
 * Parse and evaluate a string containing literal JSON or a JSONata expression.
 * @param expr Expression to evaluate.
 * @param data Data to use when evaluating the expression.
 *
 * @returns The result of the evaluation.
 *
 */
export function evalExpression(expr: string, data: unknown = {}): Promise<unknown> {
  return jsonata(expr).evaluate(data);
}
